import React from 'react';
import moment from 'moment';
import {
  Box,
  Grid,
  Popover,
  MenuItem,
  TextField,
} from '@mui/material';
import Input from '../Input';
import DrawerActions from '../DrawerActions';
import { tabNumber, tabByNumber, lastOnesOptions, Choices } from './consts';
import { getRange, initialDateInputProps } from './props';
import TabsPanel from './TabsPanel';

// Meses em pt-br fixos pra evitar depender de moment.locale (que em alguns
// paths de carregamento volta pra inglês com o DatePicker do MUI X).
const MONTHS_PT_BR = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

/**
 * Substitui o `<DatePicker views={['year', 'month']}>` do MUI X que
 * teimava em mostrar meses em inglês (MAY etc) mesmo com locale pt-br.
 * Dois selects nativos do MUI — mais simples, mais previsível, 100% pt-br.
 */
function MonthYearSelect(props) {
  const { value, onChange } = props;
  const current = value ? moment(value) : moment();
  const currentMonth = current.month(); // 0..11
  const currentYear = current.year();

  // Faixa de anos: -10 anos passados até +5 anos no futuro.
  const minYear = currentYear - 10;
  const maxYear = currentYear + 5;
  const years = [];
  for (let y = minYear; y <= maxYear; y += 1) years.push(y);

  const emit = (month, year) => {
    onChange(moment({ year, month, day: 1 }));
  };

  return (
    <Grid container spacing={1}>
      <Grid size={7}>
        <TextField
          fullWidth
          select
          label="Mês"
          value={currentMonth}
          onChange={(e) => emit(Number(e.target.value), currentYear)}
        >
          {MONTHS_PT_BR.map((name, idx) => (
            <MenuItem key={name} value={idx}>
              {name}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid size={5}>
        <TextField
          fullWidth
          select
          label="Ano"
          value={currentYear}
          onChange={(e) => emit(currentMonth, Number(e.target.value))}
        >
          {years.map((y) => (
            <MenuItem key={y} value={y}>
              {y}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
    </Grid>
  );
}

function FilterPopover(props) {
  const {
    id,
    open,
    anchor,
    classes,
    onClose,
    commitState,
    tab,
    date,
    endDate,
    lastOnesOption,
  } = props;
  const [currentTab, setCurrentTab] = React.useState(tab);
  const [currentDate, setCurrentDate] = React.useState(date);
  const [currentEndDate, setCurrentEndDate] = React.useState(endDate);
  const [currentOption, setCurrentOption] = React.useState(lastOnesOption);
  const [newDate, newEndDate] = getRange(
    currentTab,
    currentDate,
    currentEndDate,
    currentOption,
  );

  const tabIdx = tabNumber[currentTab];
  const onFilter = commitState({
    tab: currentTab,
    option: currentOption,
    endDate: newEndDate,
    date: newDate,
  });

  return (
    <Popover
      id={id}
      open={open}
      anchorEl={anchor}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      className={classes.popover}
    >
      <TabsPanel
        currentTab={tabIdx}
        onChange={(event, index) => setCurrentTab(tabByNumber[index])}
      />
      <Grid container spacing={2} className={classes.formBody}>
        {tabIdx === 0 && (
          <>
            <Grid size={12}>
              <Input
                type="radio"
                options={lastOnesOptions}
                label="Útimos"
                value={currentOption}
                onChange={(e) => setCurrentOption(e.target.value)}
              />
            </Grid>
          </>
        )}
        {tabIdx >= 1 && currentTab !== Choices.MONTHLY && (
          <Grid size={12}>
            <Input
              type="date"
              style={{ width: '100%' }}
              {...initialDateInputProps(currentTab, setCurrentDate)}
              value={currentDate}
              noKeyboard
            />
          </Grid>
        )}
        {currentTab === Choices.MONTHLY && (
          <Grid size={12}>
            <MonthYearSelect value={currentDate} onChange={setCurrentDate} />
          </Grid>
        )}
        {tabIdx === 3 && (
          <Grid size={12}>
            <Input
              label="Até"
              type="date"
              onChange={(e) => setCurrentEndDate(e)}
              value={currentEndDate}
              noKeyboard
            />
          </Grid>
        )}
      </Grid>
      {/* Footer com Cancelar/Filtrar padronizado. Não usa `classes.formButtons`
          pra evitar conflito: a classe antiga aplica `textAlign:center` +
          margens nos botões diretos, quebrando o layout interno do
          DrawerActions (que já tem flex/gap/justify próprio). */}
      <Box
        sx={{
          px: { xs: 2, md: 2.5 },
          py: { xs: 1.5, md: 2 },
          borderTop: '1px solid rgba(0, 128, 173, 0.10)',
        }}
      >
        <DrawerActions
          cancelLabel="Cancelar"
          confirmLabel="Filtrar"
          onCancel={onClose}
          onConfirm={(e) => {
            onFilter(e);
            onClose();
          }}
        />
      </Box>
    </Popover>
  );
}

export default FilterPopover;
